import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import CabiMascot from "../../../components/Kids/CabiMascot";
import GameShell from "../../../components/Kids/GameShell";
import ProgressBar from "../../../components/Kids/ProgressBar";
import OptionTile from "../../../components/Kids/OptionTile";
import CabiBubble from "../../../components/Kids/CabiBubble";
import Celebration from "../../../components/Kids/Celebration";
import EmptyState from "../../../components/Kids/EmptyState";
import Skeleton from "../../../components/Kids/Skeleton";
import useGameSounds from "../../../hooks/useGameSounds";
import useGameContent from "../../../hooks/useGameContent";

// README game-identity gradient — Quiz · Number Ninja (brand orange)
const QUIZ_BG = "radial-gradient(130% 100% at 50% 0%, #FF8A4C 0%, #EE5F2B 55%, #D14E22 100%)";

const QUIZ_DEFAULTS = {
  emoji: "🎮",
  bg: QUIZ_BG,
  accent: "#EE5F2B",
};

const API   = process.env.REACT_APP_API_URL;
const token = () => localStorage.getItem("kids_parent_token");

const GAMES = {
  "number-ninja": {
    title: "Number Ninja", emoji: "🥷",
    bg: QUIZ_BG, accent: "#EE5F2B",
    questions: [
      { q: "What is 7 + 8?",                        opts: ["13","14","15","16"],          ans: 2 },
      { q: "What is 24 ÷ 6?",                        opts: ["3","4","5","6"],              ans: 1 },
      { q: "Which number comes next?\n3, 6, 9, ___", opts: ["10","11","12","13"],          ans: 2 },
      { q: "What is 9 × 4?",                         opts: ["32","36","38","40"],          ans: 1 },
      { q: "What is 50 − 18?",                       opts: ["28","32","34","38"],          ans: 1 },
      { q: "What is half of 80?",                    opts: ["30","35","40","45"],          ans: 2 },
    ],
  },
  "math-planet": {
    title: "Math Planet", emoji: "🪐",
    bg: "radial-gradient(130% 100% at 50% 0%, #EE5F2B 0%, #ef4444 55%, #e11d48 100%)", accent: "#EE5F2B",
    questions: [
      { q: "What is 12 × 12?",           opts: ["124","136","144","148"], ans: 2 },
      { q: "What is 15% of 200?",        opts: ["25","30","35","40"],     ans: 1 },
      { q: "If x + 9 = 17, what is x?",  opts: ["6","7","8","9"],         ans: 2 },
      { q: "Area of a square, side 8?",  opts: ["16","32","64","80"],     ans: 2 },
      { q: "3² + 4² = ?",                opts: ["14","25","30","49"],     ans: 1 },
      { q: "What is 144 ÷ 12?",          opts: ["10","11","12","13"],     ans: 2 },
    ],
  },
  "science-explorer": {
    title: "Science Explorer", emoji: "🔭",
    bg: "radial-gradient(130% 100% at 50% 0%, #EE5F2B 0%, #ea580c 55%, #eab308 100%)", accent: "#EE5F2B",
    questions: [
      { q: "What do plants use to make food?",      opts: ["Sugar","Sunlight, CO₂ & Water","Soil only","Rain only"], ans: 1 },
      { q: "How many planets in our solar system?", opts: ["7","8","9","10"],              ans: 1 },
      { q: "What gas do we breathe in?",            opts: ["Carbon dioxide","Hydrogen","Nitrogen","Oxygen"],         ans: 3 },
      { q: "The largest planet is…",                opts: ["Saturn","Neptune","Jupiter","Uranus"],                   ans: 2 },
      { q: "Day & night happen because…",           opts: ["Moon blocks sun","Earth rotates","Earth orbits","Clouds"], ans: 1 },
      { q: "Water boils at which temperature?",     opts: ["80°C","90°C","100°C","110°C"],                          ans: 2 },
    ],
  },
  "food-quiz": {
    title: "Food Quiz", emoji: "🍎",
    bg: "radial-gradient(130% 100% at 50% 0%, #ef4444 0%, #ec4899 55%, #e11d48 100%)", accent: "#ef4444",
    timer: 15,
    questions: [
      { q: "🍌 I'm yellow, curved, and monkeys love me! What fruit am I?",           opts: ["Mango","Banana","Lemon","Pineapple"], ans: 1, tip: "It's a Banana! They grow towards the sun!" },
      { q: "🥕 I'm orange, crunchy, and great for your eyes! What am I?",             opts: ["Sweet Potato","Pumpkin","Carrot","Corn"], ans: 2, tip: "It's a Carrot! Full of vitamins!" },
      { q: "🍉 I'm big, green outside, red inside, and 92% water! What am I?",       opts: ["Watermelon","Apple","Pomegranate","Tomato"], ans: 0, tip: "It's a Watermelon! Nature's juice box!" },
      { q: "🧅 I make you cry when you cut me! What vegetable am I?",                 opts: ["Garlic","Potato","Onion","Ginger"], ans: 2, tip: "It's an Onion! They release a gas that stings!" },
      { q: "🍍 I take 3 years to grow and have spiky leaves on top! What am I?",      opts: ["Coconut","Pineapple","Papaya","Mango"], ans: 1, tip: "It's a Pineapple! Worth the wait!" },
      { q: "🥝 I'm brown and fuzzy outside but bright green inside! What am I?",      opts: ["Coconut","Avocado","Kiwi","Sapodilla"], ans: 2, tip: "It's a Kiwi! Fuzzy on the outside!" },
      { q: "🍒 I grow in pairs on trees and I'm small and red! What fruit am I?",     opts: ["Raspberry","Strawberry","Cherry","Cranberry"], ans: 2, tip: "It's a Cherry! They grow in pairs!" },
      { q: "🥦 I look like a little green tree! What vegetable am I?",                 opts: ["Lettuce","Cabbage","Broccoli","Spinach"], ans: 2, tip: "It's Broccoli! Tiny green trees!" },
      { q: "🌶️ I'm red, very spicy, and you need to handle me with care!",            opts: ["Capsicum","Tomato","Red Chilli","Beetroot"], ans: 2, tip: "It's a Red Chilli! Hot hot hot!" },
      { q: "🍇 I grow in bunches on vines and can be green or purple!",                opts: ["Olives","Cherries","Blueberries","Grapes"], ans: 3, tip: "It's Grapes! They grow in bunches!" },
    ],
  },
  "vehicle-quiz": {
    title: "Vehicle Quiz", emoji: "🚙",
    bg: "radial-gradient(130% 100% at 50% 0%, #6366f1 0%, #a855f7 55%, #7c3aed 100%)", accent: "#7c3aed",
    timer: 15,
    questions: [
      { q: "🚑 I rush sick people to the hospital with a loud siren! What vehicle am I?", opts: ["Bus","Ambulance","Police Car","Taxi"], ans: 1, tip: "It's an Ambulance! They save lives every day!" },
      { q: "🚂 I run on tracks and pull wagons full of goods! What am I?",                opts: ["Bus","Truck","Cargo Train","Aeroplane"], ans: 2, tip: "It's a Cargo Train! Choo choo!" },
      { q: "🚁 I can fly straight up and hover in one place! What vehicle am I?",         opts: ["Aeroplane","Glider","Helicopter","Balloon"], ans: 2, tip: "It's a Helicopter! They can go up, down, and sideways!" },
      { q: "🛶 I'm a small boat you move using a paddle with your arms! What am I?",     opts: ["Yacht","Kayak","Cruise Ship","Jet Ski"], ans: 1, tip: "It's a Kayak! Great exercise on the water!" },
      { q: "🚄 I can go over 300 km per hour on rail tracks! What am I?",                opts: ["Tram","Cargo Train","Monorail","Bullet Train"], ans: 3, tip: "It's a Bullet Train! One of the fastest vehicles on Earth!" },
      { q: "🚒 I carry water hoses and ladders to fight fires! What vehicle am I?",      opts: ["Ambulance","Police Car","Fire Brigade","Tow Truck"], ans: 2, tip: "It's a Fire Brigade! Brave firefighters ride these!" },
      { q: "🏗️ I dig huge holes in the ground with my giant arm! What am I?",             opts: ["Tractor","Excavator","Road Roller","Loader"], ans: 1, tip: "It's an Excavator! They can dig very deep!" },
      { q: "🛳️ I'm like a floating city with pools and restaurants! What am I?",          opts: ["Cargo Ship","Houseboat","Cruise Ship","Catamaran"], ans: 2, tip: "It's a Cruise Ship! People take holidays on these!" },
      { q: "🚀 I blast off into outer space beyond the clouds! What vehicle am I?",       opts: ["Fighter Jet","Glider","Aeroplane","Space Shuttle"], ans: 3, tip: "It's a Space Shuttle! Astronauts ride these to space!" },
      { q: "🚜 I help farmers plow fields and harvest crops! What am I?",                 opts: ["Truck","Loader","Tractor","Dump Truck"], ans: 2, tip: "It's a Tractor! Essential for farming!" },
    ],
  },
};

// README quiz-answer set — A/B/C/D tile colours
const QUIZ_COLORS  = ["#F2506E", "#4F86E0", "#37A65B", "#8B5CF6"];
const QUIZ_LETTERS = ["A", "B", "C", "D"];

const DEFAULT_TIMER_SECS = 15;

const CABI_TIPS = {
  correct: ["Amazing! You nailed it! 🌟", "That's right! You're a star! ⭐", "Brilliant work! Keep it up! 🎉", "Wow, you're so smart! 🧠"],
  wrong:   ["Oops! Try to remember this one 💪", "Almost! You'll get it next time 🤗", "Don't give up — keep going! 🚀", "Good try! Keep practising 📚"],
  timeout: ["Time's up! You can do it next round ⏰", "A bit more speed next time! ⚡"],
};

const randTip = (arr) => arr[Math.floor(Math.random() * arr.length)];

// Freshly re-ordered every time it's called (question order AND each
// question's option order) so the quiz never plays out the same way twice —
// answers were previously always in the same position, letting a kid learn
// "it's option C" instead of the actual content.
function shuffleQuestions(questions) {
  const shuffled = [...questions].sort(() => Math.random() - 0.5);
  return shuffled.map((q) => {
    if (!Array.isArray(q.opts)) return q;
    const order = q.opts.map((_, i) => i).sort(() => Math.random() - 0.5);
    return { ...q, opts: order.map((i) => q.opts[i]), ans: order.indexOf(q.ans) };
  });
}

export default function QuizGame() {
  const { slug }  = useParams();
  const [sp]      = useSearchParams();
  const navigate  = useNavigate();
  const sounds    = useGameSounds();
  const { t }     = useTranslation();

  const gameId   = sp.get("gameId");
  const childId  = sp.get("childId") || sessionStorage.getItem("childId");
  const childName = sessionStorage.getItem("childName") || "Friend";

  // Built-in games resolve instantly; admin-created games load their content.
  const { game, resolving, notFound } = useGameContent({
    slug, gameId, hardcoded: GAMES, defaults: QUIZ_DEFAULTS,
  });
  // `shuffleNonce` forces a fresh shuffle on replay even though `game` itself
  // hasn't changed — `useMemo` so the shuffled list is ready on the very
  // first render, no flash of the "couldn't load" empty-state.
  const [shuffleNonce, setShuffleNonce] = useState(0);
  const questions = useMemo(
    () => (game?.questions?.length ? shuffleQuestions(game.questions) : []),
    [game, shuffleNonce]
  );
  // Per-game timer (admin can set `timer` in the quiz content); falls back to 15s.
  const quizTimer = Number(game?.timer) > 0 ? Number(game.timer) : DEFAULT_TIMER_SECS;

  const [qIndex,   setQIndex]   = useState(0);
  const [selected, setSelected] = useState(null);
  const [score,    setScore]    = useState(0);
  const [timer,    setTimer]    = useState(DEFAULT_TIMER_SECS);
  const [phase,    setPhase]    = useState("playing");
  const [reward,   setReward]   = useState(null);
  const [levelUpSeen, setLevelUpSeen] = useState(false);
  const [stars,    setStars]    = useState([]);
  const [cabiMood, setCabiMood] = useState("idle");
  const [cabiTip,  setCabiTip]  = useState("Let's play! Choose the right answer! 🎮");

  const timerRef  = useRef(null);
  const startTime = useRef(Date.now());

  useEffect(() => {
    if (phase !== "playing" || !questions.length) return;
    // NB: questions.length is in the deps so the timer also starts on the FIRST
    // question of an admin-created game (whose content loads after first render).
    setTimer(quizTimer);
    setCabiMood("thinking");
    timerRef.current = setInterval(() => {
      setTimer(t => {
        if (t <= 1) { clearInterval(timerRef.current); handleAnswer(-1); return 0; }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qIndex, phase, questions.length, quizTimer]);

  useEffect(() => {
    if (!gameId || !childId) return;
    axios.post(`${API}/api/kids/games/${childId}/start/${gameId}`, {}, {
      headers: { Authorization: `Bearer ${token()}` }
    }).catch(() => {});
  }, []);

  const spawnStars = () => {
    const s = Array.from({ length: 14 }, (_, i) => ({
      id: Date.now() + i,
      x: 35 + Math.random() * 30,
      y: 30 + Math.random() * 30,
      delay: Math.random() * 0.4,
    }));
    setStars(s);
    setTimeout(() => setStars([]), 1400);
  };

  const handleAnswer = useCallback((optIdx) => {
    clearInterval(timerRef.current);
    if (phase !== "playing") return;

    const q       = questions[qIndex];
    if (!q) return;
    const correct = optIdx === q.ans;
    const timeout = optIdx === -1;
    setSelected(optIdx);
    setPhase("feedback");

    if (correct) {
      setScore(s => s + 1);
      spawnStars();
      setCabiMood("celebrating");
      setCabiTip(randTip(CABI_TIPS.correct));
      sounds.playCorrect();
    } else if (timeout) {
      setCabiMood("thinking");
      setCabiTip(randTip(CABI_TIPS.timeout));
      sounds.playWrong();
    } else {
      setCabiMood("wrong");
      setCabiTip(randTip(CABI_TIPS.wrong));
      sounds.playWrong();
    }

    setTimeout(() => {
      const next = qIndex + 1;
      if (next < questions.length) {
        setQIndex(next);
        setSelected(null);
        setPhase("playing");
        setCabiMood("idle");
        setCabiTip("Next question! You've got this! 💡");
      } else {
        completeGame(correct ? score + 1 : score);
      }
    }, 1600);
  }, [phase, qIndex, questions, score, sounds]);

  const completeGame = async (finalScore) => {
    setPhase("complete");
    const elapsed = Math.round((Date.now() - startTime.current) / 1000);
    const pct     = Math.round((finalScore / questions.length) * 100);
    const perfect = pct === 100;
    setCabiMood(perfect ? "celebrating" : finalScore > 0 ? "happy" : "idle");
    sounds.playComplete(perfect);

    if (!gameId || !childId) return;
    try {
      const res = await axios.post(
        `${API}/api/kids/games/${childId}/complete/${gameId}`,
        { score: pct, time_spent_seconds: elapsed },
        { headers: { Authorization: `Bearer ${token()}` } }
      );
      setReward(res.data?.data);
    } catch {}
  };

  if (resolving) return (
    <GameShell title="Loading…" emoji={QUIZ_DEFAULTS.emoji} bg={QUIZ_BG} onBack={() => navigate(-1)}>
      <Skeleton className="h-2 w-full mb-4" rounded="9999px" />
      <div className="flex items-center gap-3 mb-4">
        <Skeleton className="h-14 flex-1" rounded="16px" />
        <Skeleton className="h-14 w-14 shrink-0" rounded="9999px" />
      </div>
      <Skeleton className="h-28 w-full mb-6" rounded="20px" />
      <div className="grid grid-cols-2 gap-3">
        <Skeleton className="h-16" rounded="16px" />
        <Skeleton className="h-16" rounded="16px" />
        <Skeleton className="h-16" rounded="16px" />
        <Skeleton className="h-16" rounded="16px" />
      </div>
    </GameShell>
  );

  if (notFound || !game || !questions.length) return (
    <div className="min-h-[100dvh] flex items-center justify-center" style={{ background: QUIZ_BG }}>
      <EmptyState
        theme="dark"
        mood="thinking"
        title="Oops — no internet!"
        subtitle="This game couldn't load. Check your connection and try again."
        cta="Retry"
        onCta={() => window.location.reload()}
      />
    </div>
  );

  /* ── COMPLETE SCREEN ── */
  if (phase === "complete") {
    const pct    = Math.round((score / questions.length) * 100);
    const perfect = pct === 100;
    const won     = pct >= 60;   // pass mark
    const elapsedSecs = Math.round((Date.now() - startTime.current) / 1000);
    return (
      <div className="min-h-[100dvh] overflow-hidden flex items-center justify-center p-4" style={{ background: game.bg || QUIZ_BG }}>
        <style>{`
          @keyframes pop { 0%{transform:scale(0) rotate(-10deg)} 70%{transform:scale(1.1)} 100%{transform:scale(1)} }
          @keyframes confetti { 0%{transform:translateY(-12vh) rotate(0)} 100%{transform:translateY(112vh) rotate(720deg)} }
          .pop { animation: pop 0.5s cubic-bezier(.34,1.56,.64,1) both }
          .conf { top:0; animation: confetti linear infinite }
        `}</style>

        {perfect && [...Array(22)].map((_, i) => (
          <div key={i} className="conf fixed text-2xl pointer-events-none"
            style={{ insetInlineStart: `${Math.random()*100}%`,
                     animationDuration: `${2.5 + Math.random()*2}s`,
                     animationDelay:    `${Math.random()*2.5}s` }}>
            {["⭐","🌟","✨","🎉","🎊","🏆"][Math.floor(Math.random()*6)]}
          </div>
        ))}

        {reward?.level_up && !levelUpSeen && (
          <Celebration
            variant="level"
            title="Level Up!"
            subtitle={`You're now Level ${reward.level}!`}
            cta="Continue"
            onContinue={() => setLevelUpSeen(true)}
          />
        )}

        <div className="bg-white rounded-card-lg p-8 max-w-sm w-full text-center shadow-sheet pop">
          <CabiMascot mood={cabiMood} size={120} className="mx-auto mb-2" />

          {/* Clear win / loss verdict */}
          <div className={`inline-block px-4 py-1.5 rounded-full text-sm font-extrabold mb-2 ${
            won ? "bg-[#E7F6EC] text-[#22874E]" : "bg-red-100 text-danger"}`}>
            {won ? "🎉 YOU WON!" : "😅 YOU LOST"}
          </div>
          <h2 className="text-2xl font-semibold text-ink mb-1 font-display">
            {perfect ? "Perfect Score! 🏆" : won ? "Great job! 🌟" : "Keep practising! 💪"}
          </h2>
          <p className="text-muted mb-1">
            {childName} got <span className="font-bold" style={{ color: game.accent || "#EE5F2B" }}>{score}</span> out of {questions.length} correct
          </p>
          <p className="text-faint text-sm mb-5">
            {questions.length - score} wrong · ⏱ {elapsedSecs}s · pass mark 60%
          </p>

          <div className="relative w-28 h-28 mx-auto mb-5">
            <svg className="w-full h-full -rotate-90" viewBox="0 0 36 36">
              <circle cx="18" cy="18" r="15.9" fill="none" stroke="#e5e7eb" strokeWidth="3" />
              <circle cx="18" cy="18" r="15.9" fill="none" stroke={game.accent || "#EE5F2B"} strokeWidth="3"
                strokeDasharray={`${pct} 100`} strokeLinecap="round" />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-2xl font-bold font-display" style={{ color: game.accent || "#EE5F2B" }}>{pct}%</span>
            </div>
          </div>

          {reward && (
            <div className="grid grid-cols-3 gap-3 mb-5">
              <RewardChip icon="⭐" label="XP"     value={`+${reward.xp_earned}`}    color="yellow" />
              <RewardChip icon="🪙" label="Coins"  value={`+${reward.coins_earned}`} color="amber"  />
              <RewardChip icon="🔥" label="Streak" value={`${reward.streak_days}d`}  color="orange" />
            </div>
          )}
          {reward?.watch_minutes_earned > 0 && (
            <button onClick={() => navigate(`/kids/watch?childId=${childId}`)}
              className="w-full bg-violet-50 border border-violet-200 rounded-xl p-3 mb-4 text-sm font-bold text-violet-700 hover:bg-violet-100 transition">
              📺 You earned +{reward.watch_minutes_earned} watch minutes! Tap to watch →
            </button>
          )}

          <div className="flex gap-3">
            <button onClick={() => { setShuffleNonce((n) => n + 1); setQIndex(0); setScore(0); setSelected(null); setPhase("playing"); setCabiMood("idle"); setReward(null); setLevelUpSeen(false); startTime.current = Date.now(); }}
              className="flex-1 bg-faint/20 hover:bg-faint/30 text-ink font-semibold py-3 rounded-2xl transition font-display">
              🔄 {t("kids.games.quiz.playAgain")}
            </button>
            <button onClick={() => navigate(-1)}
              className="flex-1 text-white font-semibold py-3 rounded-2xl shadow-primary transition font-display"
              style={{ background: game.accent || "#EE5F2B" }}>
              🎮 More Games
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* ── QUIZ SCREEN ── */
  const q = questions[qIndex];
  const segmented = questions.map((_, i) => (i < qIndex ? "done" : i === qIndex ? "active" : "pending"));

  return (
    <GameShell
      title={game.title}
      emoji={game.emoji}
      bg={game.bg || QUIZ_BG}
      onBack={() => navigate(-1)}
      right={
        <span className="rounded-full px-3.5 min-h-[36px] inline-flex items-center font-display font-semibold text-[13px] text-white"
          style={{ background: "rgba(0,0,0,.16)" }}>
          ⭐ {score}/{questions.length}
        </span>
      }
    >
      <style>{`
        @keyframes shake     { 0%,100%{transform:translateX(0)} 20%,60%{transform:translateX(-8px)} 40%,80%{transform:translateX(8px)} }
        @keyframes star-pop  { 0%{opacity:1;transform:scale(0)} 50%{opacity:1;transform:scale(1.5)} 100%{opacity:0;transform:scale(1) translateY(-60px)} }
        @keyframes slide-in  { from{transform:translateY(30px);opacity:0} to{transform:translateY(0);opacity:1} }
        .star-burst { animation: star-pop 1s ease forwards }
        .shake      { animation: shake 0.4s ease }
        .slide-in   { animation: slide-in 0.4s ease }
      `}</style>

      {stars.map(s => (
        <div key={s.id} className="fixed pointer-events-none z-50 text-2xl star-burst"
          style={{ insetInlineStart: `${s.x}%`, top: `${s.y}%`, animationDelay: `${s.delay}s` }}>⭐</div>
      ))}

      <ProgressBar segmented={segmented} className="mb-4" />

      {/* Cabi Mascot + Tip + circular timer */}
      <div className="flex items-center gap-3 mb-4">
        <CabiBubble mood={cabiMood} text={cabiTip} className="flex-1" />
        <div
          className="shrink-0 flex items-center justify-center rounded-full font-display font-semibold text-[18px] text-white"
          style={{ width: 56, height: 56, background: "rgba(0,0,0,.16)", border: "3px solid #FFD54A" }}
        >
          {timer}
        </div>
      </div>

      {/* Question */}
      <main className="flex-1 min-h-0 overflow-y-auto flex flex-col justify-center w-full">
        <div className="slide-in mb-6 rounded-card-lg p-6 text-center" style={{ background: "rgba(255,255,255,.16)" }}>
          <div className="text-4xl mb-3">{game.emoji}</div>
          <p className="text-white font-display font-semibold text-xl leading-tight whitespace-pre-line">{q.q}</p>
        </div>

        <div className="grid grid-cols-2 gap-3">
          {q.opts.map((opt, i) => {
            const isAns = i === q.ans;
            const isSel = selected === i;
            let state = "idle";
            if (phase === "feedback") state = isAns ? "correct" : "dim";

            return (
              <OptionTile
                key={i}
                letter={QUIZ_LETTERS[i]}
                color={QUIZ_COLORS[i]}
                state={state}
                onClick={() => { if (phase === "playing") { sounds.playClick(); handleAnswer(i); } }}
                className={isSel && !isAns && phase === "feedback" ? "shake" : ""}
              >
                {opt}
              </OptionTile>
            );
          })}
        </div>
      </main>
    </GameShell>
  );
}

function RewardChip({ icon, label, value, color }) {
  const colors = {
    yellow: "bg-yellow-50 text-yellow-700",
    amber:  "bg-amber-50  text-amber-700",
    orange: "bg-orange-50 text-[#EE5F2B]",
  };
  return (
    <div className={`${colors[color]} rounded-xl p-2 text-center`}>
      <div className="text-xl">{icon}</div>
      <div className="font-extrabold text-sm">{value}</div>
      <div className="text-xs opacity-70">{label}</div>
    </div>
  );
}
